import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import { type ChangeEvent, type JSX, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { AsyncSearchUnionProps } from '../types';

const StyledTextField = styled(TextField)`
  & .MuiInputBase-root {
    border-width: 0px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.background.grayLight};
  }
`;

const StyledCloseIconButton = styled(IconButton)`
  position: absolute;
  right: ${props => props.theme.spacing(1)};
`;

export type SearchBarProps = {
  searchInput: string;
  setSearchInput: (value: string) => void;
  customPlaceholder?: string;
} & AsyncSearchUnionProps;

export const SearchBar = (props: SearchBarProps): JSX.Element => {
  const { searchInput, setSearchInput, isAsyncSearch, customPlaceholder } = props;
  const { t } = useTranslation();
  const debouncedSearch = useRef(
    isAsyncSearch
      ? debounce(searchInput => {
          props.asyncSearchFn(searchInput);
        }, 300)
      : null
  ).current;

  const placeholder = customPlaceholder || t('Search name');

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);

      if (isAsyncSearch && debouncedSearch) {
        debouncedSearch(e.target.value);
      }
    },
    [isAsyncSearch, debouncedSearch, setSearchInput]
  );

  const onClearSearch = useCallback(() => {
    setSearchInput('');

    if (isAsyncSearch && debouncedSearch) {
      debouncedSearch('');
    }
  }, [isAsyncSearch, debouncedSearch, setSearchInput]);

  return (
    <Grid container size={12} sx={{ height: theme => theme.spacing(4) }}>
      <StyledTextField
        placeholder={placeholder}
        onChange={onInputChange}
        value={searchInput}
        fullWidth
        data-test="search-field"
        variant="outlined"
        slotProps={{
          input: {
            endAdornment: searchInput && (
              <StyledCloseIconButton size="small" data-test="close-search" onClick={onClearSearch}>
                <CloseIcon />
              </StyledCloseIconButton>
            ),
          },
        }}
      />
    </Grid>
  );
};
